import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import AuthShell from "../components/public/AuthShell";

const EmailConfirmation = () => {
  const [statusMessage, setStatusMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const userId = searchParams.get("UserId") || searchParams.get("userId");
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    let redirect;
    const confirmEmail = async () => {
      if (!userId || !token) {
        setStatusMessage("This confirmation link is incomplete.");
        setFailed(true);
        setIsLoading(false);
        return;
      }

      try {
        // Don't encode the token again - it's already URL-encoded
        const response = await axiosInstance.get(`/User/ConfirmEmail?userId=${userId}&token=${token}`, {
          headers: { Authorization: undefined, Accept: "text/plain" },
        });

        setStatusMessage(typeof response.data === "string" && response.data ? response.data : "Your email has been confirmed.");
        setFailed(false);

        if (response.status === 200) {
          redirect = setTimeout(() => navigate("/login", { state: { emailConfirmed: true } }), 3000);
        }
      } catch (error) {
        let errorMessage = "We couldn't confirm your email.";
        if (error.response) {
          errorMessage = typeof error.response.data === "string" ? error.response.data : error.response.data?.message || errorMessage;
        }
        setStatusMessage(errorMessage);
        setFailed(true);
      } finally {
        setIsLoading(false);
      }
    };

    confirmEmail();
    return () => clearTimeout(redirect);
  }, [userId, token, navigate]);

  const handleResendEmail = async () => {
    if (!email) {
      setStatusMessage("We don't have an email address to resend to.");
      return;
    }

    try {
      setResending(true);
      const response = await axiosInstance.post("/auth/resend-confirmation", { email });
      setStatusMessage(response.data.message || "Confirmation email sent. Check your inbox.");
      setFailed(false);
    } catch (error) {
      setStatusMessage(error.response?.data?.message || "Failed to resend the confirmation email. Please try again later.");
    } finally {
      setResending(false);
    }
  };

  if (isLoading) {
    return (
      <AuthShell title="Confirming your email" subtitle="One moment...">
        <div className="auth-spinner" role="status" aria-label="Confirming" />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={failed ? "We couldn't confirm your email" : "Email confirmed"}
      subtitle={statusMessage}
      status={failed ? "error" : "success"}
    >
      <div className="auth-stack">
        {failed && (email ? (
          <button type="button" className="auth-btn auth-btn--block" onClick={handleResendEmail} disabled={resending}>
            {resending ? "Sending..." : "Resend confirmation email"}
          </button>
        ) : (
          <button type="button" className="auth-btn auth-btn--block" onClick={() => navigate("/resend-confirmation")}>
            Get a new link
          </button>
        ))}
        <button type="button" className={`auth-btn auth-btn--block ${failed ? "auth-btn--ghost" : ""}`} onClick={() => navigate("/login")}>
          {failed ? "Go to sign in" : "Sign in now"}
        </button>
      </div>
      {!failed && <p className="auth-hint center">Taking you to sign in...</p>}
    </AuthShell>
  );
};

export default EmailConfirmation;
